import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getFleetWithCurrencyByCity } from "../apis/reservations/apis";
import { isSuperAdmin } from "../utils/utils";

/**
 * V1 Redux-based hook for fetching fleets by payment configuration.
 * Note: V2 should use the TanStack Query version from @/v2/queries/fleets
 */
const useFleetsByPaymentConfig = () => {
  const [fleetsByPaymentConfig, setFleetsByPaymentConfig] = useState({});
  const [fleetCurrencies, setFleetCurrencies] = useState({});
  const [loading, setLoading] = useState(true);

  const adminDetails = useSelector((state) => state.auth.adminDetails);
  const cityWhitelabelMapping = useSelector(
    (state) => state.cities.cityWhitelabelMapping
  );

  useEffect(() => {
    if (!cityWhitelabelMapping) return;

    const fetchFleets = async () => {
      setLoading(true);
      const cityIds = new Set();

      if (isSuperAdmin(adminDetails)) {
        cityWhitelabelMapping.forEach((whitelabel) => {
          whitelabel.cities?.forEach((city) => cityIds.add(city.id));
        });
      } else {
        const adminWhitelabelId = adminDetails?.whiteLabelId;
        const whitelabel = cityWhitelabelMapping.find(
          (w) => w.id === adminWhitelabelId
        );
        whitelabel?.cities?.forEach((city) => cityIds.add(city.id));
      }

      const fleetMap = {};
      const currencyMap = {};
      await Promise.all(
        Array.from(cityIds).map(async (cityId) => {
          try {
            const { fleet, currency } =
              await getFleetWithCurrencyByCity(cityId);
            if (
              fleet?.payment_configuration_id &&
              !fleetMap[fleet.payment_configuration_id]
            ) {
              fleetMap[fleet.payment_configuration_id] = fleet;
              if (currency) {
                currencyMap[fleet.payment_configuration_id] = currency;
              }
            }
          } catch (e) {
            console.error(`Failed to fetch fleet for city ${cityId}:`, e);
          }
        })
      );

      setFleetsByPaymentConfig(fleetMap);
      setFleetCurrencies(currencyMap);
      setLoading(false);
    };

    fetchFleets();
  }, [cityWhitelabelMapping, adminDetails]);

  const getFleetName = (paymentConfigId) => {
    const fleet = fleetsByPaymentConfig[paymentConfigId];
    if (fleet) return fleet.name;
    return paymentConfigId ? `Config ${paymentConfigId.slice(0, 8)}...` : "-";
  };

  const getCurrency = (paymentConfigId) => {
    return fleetCurrencies[paymentConfigId] || null;
  };

  return {
    fleetsByPaymentConfig,
    fleetCurrencies,
    loading,
    getFleetName,
    getCurrency,
  };
};

export default useFleetsByPaymentConfig;
